import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Printer, ArrowLeft, Info, Clapperboard } from "lucide-react";
import Reveal from "@/components/Reveal";

const STROKE = "#151515";

function SheetLetters() {
  return (
    <svg viewBox="0 0 600 780" className="h-auto w-full" role="img" aria-label="Coloring sheet: BUBZ and BUBBLES logo letters">
      <rect x="10" y="10" width="580" height="760" rx="24" fill="#fff" stroke={STROKE} strokeWidth="3" strokeDasharray="10 8" />
      <text x="300" y="80" textAnchor="middle" fontFamily="Fredoka, sans-serif" fontWeight="600" fontSize="26" fill="#fff" stroke={STROKE} strokeWidth="1.6" letterSpacing="4">COLOR THE LOGO!</text>
      <text x="300" y="245" textAnchor="middle" fontFamily="Fredoka, sans-serif" fontWeight="700" fontSize="130" fill="#fff" stroke={STROKE} strokeWidth="3.5">BUBZ</text>
      <text x="300" y="330" textAnchor="middle" fontFamily="Fredoka, sans-serif" fontWeight="700" fontSize="64" fill="#fff" stroke={STROKE} strokeWidth="3">&amp;</text>
      <text x="300" y="450" textAnchor="middle" fontFamily="Fredoka, sans-serif" fontWeight="700" fontSize="88" fill="#fff" stroke={STROKE} strokeWidth="3.5">BUBBLES</text>
      <g fill="#fff" stroke={STROKE} strokeWidth="5">
        <circle cx="95" cy="150" r="36" /><circle cx="505" cy="140" r="28" />
        <circle cx="520" cy="330" r="42" /><circle cx="80" cy="360" r="30" />
        <circle cx="130" cy="520" r="26" /><circle cx="470" cy="540" r="34" />
      </g>
      <g fill="none" stroke={STROKE} strokeWidth="4" strokeLinecap="round">
        <path d="M76 136a32 32 0 0 1 13-14" /><path d="M490 128a25 25 0 0 1 10-11" />
        <path d="M498 312a38 38 0 0 1 15-16" /><path d="M63 348a27 27 0 0 1 11-12" />
        <path d="M115 508a24 24 0 0 1 10-10" /><path d="M450 524a30 30 0 0 1 13-13" />
      </g>
      <g stroke={STROKE} strokeWidth="6" fill="#fff">
        <circle cx="300" cy="620" r="70" />
        <path d="M262 578a64 64 0 0 1 24-26" fill="none" strokeLinecap="round" />
        <line x1="300" y1="692" x2="300" y2="745" strokeLinecap="round" />
      </g>
    </svg>
  );
}

function SheetParty() {
  return (
    <svg viewBox="0 0 600 780" className="h-auto w-full" role="img" aria-label="Coloring sheet: Bubble Pop Party with cake and balloons">
      <rect x="10" y="10" width="580" height="760" rx="24" fill="#fff" stroke={STROKE} strokeWidth="3" strokeDasharray="10 8" />
      <text x="300" y="72" textAnchor="middle" fontFamily="Fredoka, sans-serif" fontWeight="700" fontSize="40" fill="#fff" stroke={STROKE} strokeWidth="2" letterSpacing="2">BUBBLE POP PARTY!</text>
      <path d="M40 130 Q300 200 560 130" fill="none" stroke={STROKE} strokeWidth="5" />
      <g fill="#fff" stroke={STROKE} strokeWidth="5" strokeLinejoin="round">
        {[
          [95, 147], [172, 163], [250, 171], [328, 171], [406, 163], [483, 147],
        ].map(([x, y], i) => (
          <path key={i} d={`M ${x - 17} ${y} L ${x + 17} ${y} L ${x} ${y + 36} Z`} />
        ))}
      </g>
      <g fill="#fff" stroke={STROKE} strokeWidth="6">
        <ellipse cx="100" cy="330" rx="42" ry="55" />
        <ellipse cx="500" cy="315" rx="42" ry="55" />
      </g>
      <path d="M92 384 l-9 13 h18 Z" fill="#fff" stroke={STROKE} strokeWidth="5" strokeLinejoin="round" />
      <path d="M508 369 l-9 13 h18 Z" fill="#fff" stroke={STROKE} strokeWidth="5" strokeLinejoin="round" />
      <path d="M100 400 q 14 45 -6 90" fill="none" stroke={STROKE} strokeWidth="4" strokeLinecap="round" />
      <path d="M500 385 q -14 45 6 90" fill="none" stroke={STROKE} strokeWidth="4" strokeLinecap="round" />
      <ellipse cx="300" cy="668" rx="155" ry="18" fill="#fff" stroke={STROKE} strokeWidth="6" />
      <rect x="205" y="530" width="190" height="128" rx="16" fill="#fff" stroke={STROKE} strokeWidth="6" />
      <path d="M205 560 q 24 24 48 0 t 48 0 t 47 0 t 47 0" fill="none" stroke={STROKE} strokeWidth="5" strokeLinecap="round" />
      <g fill="#fff" stroke={STROKE} strokeWidth="5">
        <rect x="240" y="470" width="13" height="60" rx="5" />
        <rect x="293" y="470" width="13" height="60" rx="5" />
        <rect x="346" y="470" width="13" height="60" rx="5" />
      </g>
      <g fill="#fff" stroke={STROKE} strokeWidth="5">
        <path d="M246 468 q -11 -18 0 -32 q 11 14 0 32 Z" />
        <path d="M299 468 q -11 -18 0 -32 q 11 14 0 32 Z" />
        <path d="M352 468 q -11 -18 0 -32 q 11 14 0 32 Z" />
      </g>
      <g fill="#fff" stroke={STROKE} strokeWidth="4">
        <circle cx="150" cy="470" r="7" /><circle cx="180" cy="250" r="7" />
        <circle cx="430" cy="230" r="7" /><circle cx="455" cy="480" r="7" />
        <circle cx="300" cy="270" r="7" /><circle cx="230" cy="330" r="7" />
        <circle cx="370" cy="320" r="7" /><circle cx="140" cy="560" r="7" />
        <circle cx="470" cy="570" r="7" />
      </g>
    </svg>
  );
}

function SheetRainbow() {
  return (
    <svg viewBox="0 0 600 780" className="h-auto w-full" role="img" aria-label="Coloring sheet: the big rainbow mystery">
      <rect x="10" y="10" width="580" height="760" rx="24" fill="#fff" stroke={STROKE} strokeWidth="3" strokeDasharray="10 8" />
      <text x="300" y="72" textAnchor="middle" fontFamily="Fredoka, sans-serif" fontWeight="700" fontSize="33" fill="#fff" stroke={STROKE} strokeWidth="2" letterSpacing="2">THE BIG RAINBOW MYSTERY</text>
      <g fill="none" stroke={STROKE} strokeWidth="6">
        <path d="M70 520 A 230 230 0 0 1 530 520" />
        <path d="M105 520 A 195 195 0 0 1 495 520" />
        <path d="M140 520 A 160 160 0 0 1 460 520" />
        <path d="M175 520 A 125 125 0 0 1 425 520" />
        <path d="M210 520 A 90 90 0 0 1 390 520" />
      </g>
      <g fill="#fff" stroke={STROKE} strokeWidth="6">
        <circle cx="45" cy="515" r="26" /><circle cx="80" cy="508" r="32" /><circle cx="112" cy="518" r="24" />
        <circle cx="488" cy="518" r="24" /><circle cx="520" cy="508" r="32" /><circle cx="555" cy="515" r="26" />
      </g>
      <g stroke={STROKE} fill="#fff">
        <circle cx="500" cy="150" r="40" strokeWidth="6" />
        <g strokeWidth="5" strokeLinecap="round">
          <line x1="500" y1="92" x2="500" y2="76" /><line x1="558" y1="150" x2="574" y2="150" />
          <line x1="541" y1="109" x2="553" y2="97" /><line x1="541" y1="191" x2="553" y2="203" />
        </g>
      </g>
      <g fill="#fff" stroke={STROKE} strokeWidth="5" strokeLinejoin="round">
        <path d="M120 190 l6 14 14 6 -14 6 -6 14 -6 -14 -14 -6 14 -6 Z" />
        <path d="M230 130 l6 14 14 6 -14 6 -6 14 -6 -14 -14 -6 14 -6 Z" />
        <path d="M80 300 l5 12 12 5 -12 5 -5 12 -5 -12 -12 -5 12 -5 Z" />
      </g>
      <g fill="#fff" stroke={STROKE} strokeWidth="5">
        <circle cx="150" cy="600" r="26" /><circle cx="470" cy="610" r="30" /><circle cx="300" cy="640" r="22" />
      </g>
      <g fill="none" stroke={STROKE} strokeWidth="4" strokeLinecap="round">
        <path d="M136 588a24 24 0 0 1 10-10" /><path d="M454 597a27 27 0 0 1 11-11" /><path d="M289 631a20 20 0 0 1 8-8" />
      </g>
      <path d="M20 710 Q300 665 580 710" fill="none" stroke={STROKE} strokeWidth="6" strokeLinecap="round" />
    </svg>
  );
}

function SheetFly() {
  return (
    <svg viewBox="0 0 600 780" className="h-auto w-full" role="img" aria-label="Coloring sheet: Bubz learns to fly with Bubble">
      <rect x="10" y="10" width="580" height="760" rx="24" fill="#fff" stroke={STROKE} strokeWidth="3" strokeDasharray="10 8" />
      <text x="300" y="72" textAnchor="middle" fontFamily="Fredoka, sans-serif" fontWeight="700" fontSize="40" fill="#fff" stroke={STROKE} strokeWidth="2" letterSpacing="2">UP, UP AND AWAY!</text>
      <g fill="none" stroke={STROKE} strokeWidth="6" strokeLinecap="round">
        <path d="M110 180 q 14 -16 28 0 q 14 -16 28 0" />
        <path d="M200 130 q 12 -14 24 0 q 12 -14 24 0" />
        <path d="M450 200 q 14 -16 28 0 q 14 -16 28 0" />
      </g>
      <g fill="#fff" stroke={STROKE} strokeWidth="6">
        <circle cx="455" cy="140" r="26" /><circle cx="490" cy="140" r="32" /><circle cx="522" cy="146" r="24" />
        <circle cx="95" cy="290" r="22" /><circle cx="124" cy="290" r="27" /><circle cx="150" cy="295" r="20" />
      </g>
      <circle cx="300" cy="390" r="135" fill="#fff" stroke={STROKE} strokeWidth="7" />
      <path d="M215 305a125 125 0 0 1 44-50" fill="none" stroke={STROKE} strokeWidth="7" strokeLinecap="round" />
      <circle cx="262" cy="380" r="19" fill="#fff" stroke={STROKE} strokeWidth="6" />
      <circle cx="338" cy="380" r="19" fill="#fff" stroke={STROKE} strokeWidth="6" />
      <circle cx="262" cy="383" r="7" fill={STROKE} />
      <circle cx="338" cy="383" r="7" fill={STROKE} />
      <path d="M262 440q38 36 76 0" fill="none" stroke={STROKE} strokeWidth="7" strokeLinecap="round" />
      <g fill="#fff" stroke={STROKE} strokeWidth="5">
        <circle cx="140" cy="565" r="28" /><circle cx="95" cy="625" r="21" /><circle cx="165" cy="640" r="16" />
      </g>
      <g fill="none" stroke={STROKE} strokeWidth="4" strokeLinecap="round">
        <path d="M125 552a25 25 0 0 1 10-11" /><path d="M83 615a19 19 0 0 1 8-8" /><path d="M155 632a14 14 0 0 1 6-6" />
      </g>
      <path d="M20 710 Q300 650 580 710" fill="none" stroke={STROKE} strokeWidth="6" strokeLinecap="round" />
      <g stroke={STROKE} strokeWidth="5" fill="#fff">
        <circle cx="480" cy="660" r="10" />
        <circle cx="465" cy="645" r="9" /><circle cx="495" cy="645" r="9" />
        <circle cx="465" cy="675" r="9" /><circle cx="495" cy="675" r="9" />
        <circle cx="480" cy="632" r="9" /><circle cx="480" cy="688" r="9" />
      </g>
    </svg>
  );
}

function SheetBirthday() {
  return (
    <svg viewBox="0 0 600 780" className="h-auto w-full" role="img" aria-label="Coloring sheet: Bubble's big birthday day">
      <rect x="10" y="10" width="580" height="760" rx="24" fill="#fff" stroke={STROKE} strokeWidth="3" strokeDasharray="10 8" />
      <text x="300" y="72" textAnchor="middle" fontFamily="Fredoka, sans-serif" fontWeight="700" fontSize="40" fill="#fff" stroke={STROKE} strokeWidth="2" letterSpacing="2">HAPPY BUBBLE DAY!</text>
      <circle cx="260" cy="410" r="140" fill="#fff" stroke={STROKE} strokeWidth="7" />
      <path d="M168 322a130 130 0 0 1 46-52" fill="none" stroke={STROKE} strokeWidth="7" strokeLinecap="round" />
      <circle cx="222" cy="400" r="20" fill="#fff" stroke={STROKE} strokeWidth="6" />
      <circle cx="298" cy="400" r="20" fill="#fff" stroke={STROKE} strokeWidth="6" />
      <circle cx="222" cy="403" r="7" fill={STROKE} />
      <circle cx="298" cy="403" r="7" fill={STROKE} />
      <path d="M222 462q38 36 76 0" fill="none" stroke={STROKE} strokeWidth="7" strokeLinecap="round" />
      <path d="M196 292 L260 178 L324 292 Z" fill="#fff" stroke={STROKE} strokeWidth="6" strokeLinejoin="round" />
      <circle cx="260" cy="170" r="15" fill="#fff" stroke={STROKE} strokeWidth="6" />
      <path d="M212 262 q 48 22 96 0" fill="none" stroke={STROKE} strokeWidth="5" strokeLinecap="round" />
      <g fill="#fff" stroke={STROKE} strokeWidth="6">
        <rect x="425" y="490" width="125" height="115" rx="10" />
        <rect x="414" y="456" width="147" height="38" rx="10" />
      </g>
      <line x1="487" y1="456" x2="487" y2="605" stroke={STROKE} strokeWidth="9" />
      <g fill="#fff" stroke={STROKE} strokeWidth="5">
        <circle cx="468" cy="440" r="15" /><circle cx="506" cy="440" r="15" /><circle cx="487" cy="442" r="8" />
      </g>
      <g fill="#fff" stroke={STROKE} strokeWidth="4">
        <circle cx="120" cy="180" r="7" /><circle cx="470" cy="200" r="7" />
        <circle cx="520" cy="300" r="7" /><circle cx="90" cy="330" r="7" />
        <circle cx="440" cy="120" r="7" /><circle cx="150" cy="560" r="7" />
      </g>
      <g fill="#fff" stroke={STROKE} strokeWidth="5" strokeLinejoin="round">
        <path d="M500 150 l6 14 14 6 -14 6 -6 14 -6 -14 -14 -6 14 -6 Z" />
        <path d="M110 500 l6 14 14 6 -14 6 -6 14 -6 -14 -14 -6 14 -6 Z" />
      </g>
      <g fill="#fff" stroke={STROKE} strokeWidth="5">
        <circle cx="480" cy="660" r="26" /><circle cx="110" cy="650" r="22" />
      </g>
      <g fill="none" stroke={STROKE} strokeWidth="4" strokeLinecap="round">
        <path d="M466 648a24 24 0 0 1 10-10" /><path d="M98 640a20 20 0 0 1 8-8" />
      </g>
      <path d="M20 715 Q300 675 580 715" fill="none" stroke={STROKE} strokeWidth="6" strokeLinecap="round" />
    </svg>
  );
}

const SHOW_SHEETS = [
  { id: "bubz", title: "Color Bubz!", blurb: "The world's most curious toddler, bubble wand in hand — traced from the show's artwork.", img: "/sheets/color-bubz.png" },
  { id: "bubble", title: "Color Bubble!", blurb: "Bubz's floating best friend with the biggest smile in the park.", img: "/sheets/color-bubble.png" },
  { id: "scene", title: "Best Friends at the Park", blurb: "The whole sunny park scene with Bubz, Bubble and the logo.", img: "/sheets/color-scene.png" },
  { id: "zoo", title: "A Day at the Zoo", blurb: "The tree-lined road to the zoo gates — lion, elephants and all.", img: "/sheets/color-zoo.png" },
  { id: "logo", title: "The Big Logo", blurb: "Color the BUBZ & BUBBLES bubble letters any way you like.", Svg: SheetLetters },
];

const EPISODE_SHEETS = [
  { id: "ep1-party", ep: "Ep 1 · Bubble Pop Party!", title: "Party Cake & Balloons", blurb: "The poppin'est party in the park — color the cake before someone pops it.", Svg: SheetParty },
  { id: "ep2-rainbow", ep: "Ep 2 · The Big Rainbow Mystery", title: "Follow the Rainbow", blurb: "Five rainbow bands waiting for your favorite colors.", Svg: SheetRainbow },
  { id: "ep3-fly", ep: "Ep 3 · Bubz Learns to Fly", title: "Up, Up and Away", blurb: "Bubble floating high with the birds and the clouds.", Svg: SheetFly },
  { id: "ep4-birthday", ep: "Ep 4 · Bubble's Big Day", title: "Bubble's Birthday", blurb: "A party hat, a present, and one very happy Bubble.", Svg: SheetBirthday },
];

function SheetArt({ sheet }) {
  if (sheet.img) {
    return <img src={sheet.img} alt={`${sheet.title} coloring sheet`} className="h-auto w-full" />;
  }
  return <sheet.Svg />;
}

function SheetCard({ sheet, index, onPrint }) {
  return (
    <Reveal delay={(index % 4) * 0.08}>
      <motion.article
        data-testid={`coloring-card-${sheet.id}`}
        className="flex h-full flex-col overflow-hidden rounded-[2rem] border-2 border-bubz-sky/30 bg-white shadow-[0_16px_50px_rgba(112,193,255,0.2)]"
        whileHover={{ y: -8, rotate: index % 2 === 0 ? -0.8 : 0.8 }}
        transition={{ type: "spring", stiffness: 220, damping: 18 }}
      >
        <div className="bg-bubz-cream p-4">
          <div className="overflow-hidden rounded-2xl border-2 border-bubz-ink/10 bg-white">
            <SheetArt sheet={sheet} />
          </div>
        </div>
        <div className="flex flex-1 flex-col p-6">
          {sheet.ep && (
            <span className="mb-2.5 inline-flex w-fit items-center gap-1.5 rounded-full bg-bubz-grape/15 px-3 py-1 text-[11px] font-extrabold uppercase tracking-wider text-bubz-grape">
              <Clapperboard size={11} /> {sheet.ep}
            </span>
          )}
          <h2 className="font-display text-xl font-semibold text-bubz-ink">{sheet.title}</h2>
          <p className="mt-1.5 flex-1 text-sm font-semibold text-bubz-ink/60">{sheet.blurb}</p>
          <motion.button
            data-testid={`print-sheet-${sheet.id}`}
            onClick={() => onPrint(sheet)}
            className="mt-5 flex items-center justify-center gap-2 rounded-full bg-bubz-ink px-5 py-3 font-display text-base font-semibold text-white"
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.95 }}
          >
            <Printer size={17} /> Print this sheet
          </motion.button>
        </div>
      </motion.article>
    </Reveal>
  );
}

export default function Coloring() {
  const [printing, setPrinting] = useState(null);

  const doPrint = (sheet) => {
    setPrinting(sheet);
    setTimeout(() => window.print(), 120);
  };

  return (
    <main data-testid="coloring-page" className="min-h-screen pb-20 pt-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <Link
          to="/"
          data-testid="coloring-back-home"
          className="mb-6 inline-flex items-center gap-2 rounded-full bg-white/80 px-4 py-2 text-sm font-extrabold text-bubz-ink shadow-sm transition-colors hover:bg-white"
        >
          <ArrowLeft size={15} /> Back to the park
        </Link>

        <Reveal>
          <p className="mb-3 text-xs font-extrabold uppercase tracking-[0.3em] text-bubz-tang">Print &amp; play</p>
          <h1 className="font-display text-3xl font-bold text-bubz-ink sm:text-5xl">
            Bubz &amp; Bubble <span className="text-bubz-pink">Coloring Sheets</span>
          </h1>
          <p className="mt-4 max-w-lg text-base font-semibold text-bubz-ink/60 sm:text-lg">
            Pick a sheet, hit print, and grab the crayons. A brand-new sheet arrives with every new episode!
          </p>
        </Reveal>

        <Reveal>
          <h2
            data-testid="show-sheets-heading"
            className="mt-14 flex items-center gap-3 font-display text-2xl font-bold text-bubz-ink sm:text-3xl"
          >
            Straight from the Show
            <span className="rounded-full bg-bubz-pink/15 px-3 py-1 text-xs font-extrabold uppercase tracking-wider text-bubz-pink">
              Real artwork
            </span>
          </h2>
        </Reveal>
        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {SHOW_SHEETS.map((s, i) => (
            <SheetCard key={s.id} sheet={s} index={i} onPrint={doPrint} />
          ))}
        </div>

        <Reveal>
          <h2
            data-testid="episode-sheets-heading"
            className="mt-16 flex items-center gap-3 font-display text-2xl font-bold text-bubz-ink sm:text-3xl"
          >
            Episode Sheets
            <span className="rounded-full bg-bubz-grass/20 px-3 py-1 text-xs font-extrabold uppercase tracking-wider text-bubz-ink">
              New every episode
            </span>
          </h2>
        </Reveal>
        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {EPISODE_SHEETS.map((s, i) => (
            <SheetCard key={s.id} sheet={s} index={i} onPrint={doPrint} />
          ))}
        </div>

        <p className="mt-10 flex items-center justify-center gap-2 text-center text-sm font-bold text-bubz-ink/50">
          <Info size={15} /> Sheets print on A4 or US Letter — choose "fit to page" for best results
        </p>
      </div>

      <div id="print-sheet" aria-hidden="true">
        {printing && (
          <div className="print-inner">
            <p className="print-brand">BUBZ &amp; BUBBLES · Created by J.J. STONE · www.bubzplus.com</p>
            <SheetArt sheet={printing} />
            <p className="print-foot">New 2-minute episodes every week on YouTube — @BUBZPLUSSTREAMINGSERVICE</p>
          </div>
        )}
      </div>
    </main>
  );
}
